<template>
  <div>
    <div class="container">
      <div class="box-title">
        <h4>
          <i class="fal fa-university"></i>
          ຕັ້ງຄ່າທະນາຄານ
        </h4>
      </div>

      <div class="box">
        <div v-if="isLoading" class="has-text-centered py-5">
          <i class="fal fa-spinner fa-spin fa-2x"></i>
          <p class="mt-3">ກຳລັງໂຫລດ...</p>
        </div>

        <div v-else-if="error" class="notification is-warning">
          <p>{{ error }}</p>
          <p v-if="errorRequestId" class="error-ref">ref: {{ errorRequestId }}</p>
          <vs-button @click="fetchBanks" class="mt-3">
            <i class="fal fa-redo"></i> ລອງໃໝ່
          </vs-button>
        </div>

        <div v-else-if="banks.length === 0" class="has-text-centered py-5">
          <i class="fal fa-university fa-3x has-text-grey-light"></i>
          <p class="mt-3 has-text-grey">ບໍ່ພົບຂໍ້ມູນທະນາຄານ</p>
        </div>

        <div v-else class="bank-list">
          <div
            v-for="bank in banks"
            :key="bank.code"
            :class="['bank-card', { 'bank-card--active': bank.is_active }]"
            @click="onBankClick(bank)"
          >
            <div class="bank-card__radio">
              <span :class="['radio-dot', { 'radio-dot--selected': bank.is_active }]"></span>
            </div>
            <div class="bank-card__info">
              <span class="tag is-info bank-code">{{ bank.code }}</span>
              <strong class="bank-name">{{ bank.name }}</strong>
            </div>
            <div class="bank-card__status">
              <span v-if="bank.is_active" class="tag is-success">ເປີດໃຊ້ງານ</span>
              <span v-else class="tag is-light">ປິດໃຊ້ງານ</span>
            </div>
            <div v-if="activatingBank === bank.code" class="bank-card__loading">
              <i class="fal fa-spinner fa-spin"></i>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation dialog -->
    <vs-dialog v-model="showConfirm" width="400px" prevent-close @close="cancelActivation">
      <template #header>
        <h4 class="confirm-title">
          <i class="fal fa-exchange-alt"></i> ປ່ຽນທະນາຄານ
        </h4>
      </template>
      <div class="confirm-body">
        <p>ເປີດໃຊ້ <strong>{{ pendingBank?.name }}</strong>?</p>
        <p class="confirm-warning">ທະນາຄານອື່ນຈະຖືກປິດອັດຕະໂນມັດ</p>
      </div>
      <template #footer>
        <div class="confirm-footer">
          <vs-button flat @click="cancelActivation" :disabled="activatingBank !== null">
            ຍົກເລີກ
          </vs-button>
          <vs-button @click="confirmActivation" :loading="activatingBank !== null">
            <i class="fal fa-check"></i> ຢືນຢັນ
          </vs-button>
        </div>
      </template>
    </vs-dialog>
  </div>
</template>

<script>
import walletService, { extractErrorInfo } from '@/services/walletService';
import { formatErrorMessage } from '@/services/walletFormatters';

export default {
  name: 'BankConfig',
  data() {
    return {
      banks: [],
      isLoading: false,
      error: null,
      errorRequestId: null,
      activatingBank: null,
      showConfirm: false,
      pendingBank: null,
    };
  },
  mounted() {
    this.fetchBanks();
  },
  methods: {
    async fetchBanks() {
      this.isLoading = true;
      this.error = null;
      this.errorRequestId = null;
      try {
        const response = await walletService.listBanks(this.$axios);
        this.banks = response.data || [];
      } catch (err) {
        console.error('Failed to fetch banks:', err);
        const { message, requestId } = extractErrorInfo(err);
        this.error = message || 'ບໍ່ສາມາດໂຫລດຂໍ້ມູນທະນາຄານໄດ້';
        this.errorRequestId = requestId;
      } finally {
        this.isLoading = false;
      }
    },

    onBankClick(bank) {
      // View-only roles (accounting sub-roles) can see bank status but not switch the active bank.
      // UI-only gate — the Go 'admin' JWT still authorizes activate (not enforced at Go).
      if (!this.$can(['SuperAdmin', 'accountingmanager'])) return;
      if (bank.is_active || this.activatingBank) return;
      this.pendingBank = bank;
      this.showConfirm = true;
    },

    cancelActivation() {
      this.showConfirm = false;
      this.pendingBank = null;
    },

    async confirmActivation() {
      if (!this.pendingBank) return;
      const bank = this.pendingBank;
      this.activatingBank = bank.code;

      try {
        await walletService.activateBank(this.$axios, bank.code);
        // Go automatically deactivates all others — refresh the full list
        await this.fetchBanks();
        this.$vs.notification({
          color: 'success',
          title: 'ສຳເລັດ',
          text: `ເປີດໃຊ້ງານ ${bank.name} ສຳເລັດແລ້ວ`,
        });
      } catch (err) {
        console.error('Failed to activate bank:', err);
        this.$vs.notification({
          color: 'danger',
          title: 'ເກີດຂໍ້ຜິດພາດ',
          text: formatErrorMessage(err, 'ບໍ່ສາມາດປ່ຽນສະຖານະທະນາຄານໄດ້'),
        });
      } finally {
        this.activatingBank = null;
        this.showConfirm = false;
        this.pendingBank = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.box-title {
  margin-bottom: 1rem;

  h4 {
    font-size: 1.25rem;
    font-weight: 600;
  }
}

.py-5 {
  padding-top: 3rem;
  padding-bottom: 3rem;
}

.mt-3 {
  margin-top: 1rem;
}

.error-ref {
  font-size: 12px;
  color: #856404;
  margin-top: 4px;
}

.bank-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
}

.bank-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border: 2px solid #e8e8e8;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover:not(.bank-card--active) {
    border-color: #b0b0b0;
    background: #fafafa;
  }

  &--active {
    border-color: #28a745;
    background: #f0faf3;
    cursor: default;
  }

  &__radio {
    flex-shrink: 0;
  }

  &__info {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__status {
    flex-shrink: 0;
  }

  &__loading {
    position: absolute;
    right: 20px;
    color: #666;
  }
}

.radio-dot {
  display: inline-block;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #ccc;
  transition: all 0.2s ease;

  &--selected {
    border-color: #28a745;
    background: #28a745;
    box-shadow: inset 0 0 0 4px #fff;
  }
}

.bank-code {
  font-family: monospace;
}

.bank-name {
  font-size: 1rem;
}

.confirm-title {
  font-size: 1.1rem;
  font-weight: 600;

  i {
    margin-right: 8px;
    color: #f0ad4e;
  }
}

.confirm-body {
  padding: 20px;
  text-align: center;

  p {
    margin-bottom: 8px;
    font-size: 1rem;
  }
}

.confirm-warning {
  color: #856404;
  background: #fff3cd;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.9rem;
}

.confirm-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #eee;
}
</style>
